import { createMapTitleQueryOwner, TITLE_QUERY_CHANGED } from "./map-title-query-owner.js";
import { expressionUsesTitles } from "./search-expression/title.js";
import { browserFishydexStateComposition } from "../js/fishydex/state-composition.js";
import { zoneIdentityMarkup, formatZone } from "../js/components/zone-identity.js";
import { queryInputMarkup, queryStatusMarkup, renderQueryStatus, queryStatusIndicator } from "../js/query/presentation.js";
import {
  applyQueryInputSuggestionToDraft,
  buildQueryInputDraftResultFromBundle,
  buildQueryInputFishIdentityLookup,
  buildQueryInputItemIdentityLookup,
  buildQueryInputTradeNpcIdentityLookup,
  buildQueryInputSuggestions,
  buildSearchPanelStateBundle,
  buildSemanticTermLookup,
  createQueryInputCatalogPreparedValuesTransaction,
  fishFilterTermMetadata,
  prepareQueryInputCatalogIndexes,
} from "./map-search-state.js";
import {
  buildSearchExpressionStatePatch,
  resolveSearchExpression,
  serializeSearchExpressionDsl,
} from "./map-search-contract.js";
import { mapText, currentDataLanguage } from "./map-i18n.js";
import {
  fishIdentityMarkup,
  resolveFishGrade,
} from "../js/components/fish-identity.js";
import {
  buildQuerySuggestionRowsHtml,
  escapeHtml,
  fishFilterTermIconMarkup,
  querySuggestionListKey,
  semanticIdentityMarkup,
  tradeNpcIdentityMarkup,
} from "./map-query-presentation.js";
import {
  normalizeQuerySourceCaretIndex,
  normalizeQuerySourceText,
} from "./map-query-source-text.js";
import { layoutQueryRibbon } from "../js/query/ribbon-layout.js";
import { createQueryScrollController } from "../js/query/scroll-controller.js";
import {
  FISHYMAP_BRIDGE_INPUT_APPLIED_EVENT,
  FISHYMAP_BRIDGE_INPUT_FAILED_EVENT,
  FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_EVENT,
  FISHYMAP_BRIDGE_INPUT_SUBMITTED_EVENT,
  bridgeInputReceiptIngressIsReady,
} from "./map-bridge-input-receipts.js";
import { renderSearchSelection } from "./map-search-panel.js";
import {
  createSearchCatalogPreparationOwner,
  createSearchCatalogProjector,
} from "./map-search-catalog-projection.js";
import {
  createSearchExpressionCatalogPreparedValuesTransaction,
} from "./search-expression/resolve.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import {
  FISHYMAP_QUERY_INPUT_RECEIPT_OWNER as QUERY_INPUT_RECEIPT_OWNER,
  dispatchMapQueryActivationRequest,
  dispatchMapQueryEditorStateRequest,
  dispatchMapQueryRestoredEffectsRequest,
} from "./map-ui-request-contract.js";
import { FISHYMAP_ZONE_CATALOG_READY_EVENT } from "./map-zone-catalog-live.js";
import {
  FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
} from "./map-trade-npc-catalog-live.js";
import { augmentTradeNpcQueryActivationPatch } from "./map-trade-npc-query-activation.js";
import { FISHING_CONTEST_SELECTION_CHANGE_EVENT, FISHING_CONTEST_VOTING_CLOSE_EVENT } from "../js/fishing-contest/contract.js";
import "../js/components/fishing-contest.js";

const QUERY_INPUT_TAG_NAME = "fishymap-query-input";
const HTMLElementBase = globalThis.HTMLElement ?? class {};
const QUERY_SUGGESTION_PAGE_SIZE = 12;
const QUERY_SUGGESTION_SCROLL_THRESHOLD_PX = 48;
const QUERY_STATUS_EFFECT_VARIANT_STATES = new Set([
  "evaluating",
  "applying",
  "clearing",
  "composing",
  "partial-applying",
  "invalid-clearing",
  "invalid",
  "failed",
]);

function setBooleanProperty(element, propertyName, value) {
  if (!element) {
    return;
  }
  element[propertyName] = Boolean(value);
}

function setTextContent(element, text) {
  if (!element) {
    return;
  }
  element.textContent = String(text ?? "");
}

function scheduleMicrotask(callback) {
  if (typeof globalThis.queueMicrotask === "function") {
    globalThis.queueMicrotask(callback);
    return;
  }
  Promise.resolve().then(callback);
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function cloneExpressionIssues(value) {
  return Array.isArray(value) ? cloneJson(value) : [];
}

function cloneRawExpressionTree(value) {
  return isPlainObject(value) ? cloneJson(value) : null;
}

function searchPanelSignalsFromProjection(projection) {
  if (!projection) {
    return null;
  }
  return {
    _map_ui: projection.ui,
    _map_bridged: projection.bridged,
    _map_actions: projection.actions,
    _map_bookmarks: projection.bookmarks,
    _map_character_catalog: projection.characterCatalog,
    _map_character_presence: projection.characterPresence,
    _map_character_stream: projection.characterStream,
    _map_character_activity: projection.characterActivity,
    _map_character_activity_ui: projection.characterActivityUi,
    _map_character_activity_view: projection.characterActivityView,
    _map_session: projection.session,
    _map_runtime: projection.runtime,
    _shared_fish: projection.sharedFish,
  };
}

function projectionChangesQueryDerivation(previous, next) {
  return !previous ||
    previous.ui !== next.ui ||
    previous.bridged !== next.bridged ||
    previous.runtime !== next.runtime ||
    previous.sharedFish !== next.sharedFish;
}

function draftInputSource(input) {
  return normalizeQuerySourceText(input?.value ?? "");
}

function draftInputCaretOffset(input) {
  const raw = String(input?.value ?? "");
  const fallback = draftInputSource(input).length;
  if (!Number.isInteger(input?.selectionStart)) {
    return fallback;
  }
  return normalizeQuerySourceCaretIndex(raw, input.selectionStart);
}

function setDraftInputSelection(input, requestedOffset) {
  if (!input || typeof input.setSelectionRange !== "function") return;
  const value = String(input.value ?? "");
  const offset = Math.max(0, Math.min(value.length, Number.parseInt(requestedOffset, 10) || 0));
  input.setSelectionRange(offset, offset);
}

function setDraftInputEmptyState(input, sourceText) {
  if (input?.dataset) {
    input.dataset.empty = String(sourceText ?? "").length ? "false" : "true";
  }
}

function setDraftInputValue(input, sourceText, caretOffset, options = {}) {
  if (!input) {
    return;
  }
  const source = normalizeQuerySourceText(sourceText);
  if (input.value !== source) {
    input.value = source;
  }
  setDraftInputEmptyState(input, source);
  if (options.restoreCaret !== false) {
    setDraftInputSelection(input, caretOffset);
  }
}

function ensureQueryInputMarkup(host) {
  if (host.querySelector("#fishymap-query-input-root")) {
    return;
  }
  host.innerHTML = queryInputMarkup();
}

function activeExpressionDslFromBundle(bundle) {
  const expression = bundle?.inputState?.search?.expression;
  return expression ? serializeSearchExpressionDsl(expression) : "";
}

function activeExpressionFromBundle(bundle) {
  return cloneJson(bundle?.inputState?.search?.expression || {
    type: "group",
    operator: "or",
    children: [],
  });
}

function buildQueryInputActivationSignalPatchFromResult(bundle, result, options = {}) {
  if (!result?.canApply) {
    return null;
  }
  if (result.canonicalDsl === activeExpressionDslFromBundle(bundle)) {
    return null;
  }
  const searchPatch = {};
  if (Object.prototype.hasOwnProperty.call(options, "open")) {
    searchPatch.open = options.open === true;
  }
  return augmentTradeNpcQueryActivationPatch(
    buildSearchExpressionStatePatch(result.expression, searchPatch),
    {
      signals: options.signals,
      previousExpression: bundle?.inputState?.search?.expression,
      nextExpression: result.expression,
      tradeNpcCatalog: bundle?.state?.catalog?.tradeNpcs,
    },
  );
}

export function buildQueryInputActivationSignalPatch(signals, draftText, options = {}) {
  const bundle = buildSearchPanelStateBundle(signals, options);
  const result = buildQueryInputDraftResultFromBundle(bundle, draftText);
  const patch = buildQueryInputActivationSignalPatchFromResult(bundle, result, {
    ...options,
    open: options.open !== false,
    signals,
  });
  return { result, patch };
}

function queryDraftEffectRelation(appliedSource, draftSource) {
  if (!appliedSource) {
    return "none";
  }
  if (String(appliedSource) === String(draftSource)) {
    return "current-text";
  }
  if (isAppliedSourcePrefix(appliedSource, draftSource)) {
    return "valid-prefix";
  }
  return "prior-query";
}

function queryDraftStatusText(state, context = {}) {
  const hasAppliedExpression = context.hasAppliedExpression === true;
  const suffix = hasAppliedExpression ? "active" : "empty";
  const failureKeyState = {
    clear: "failed_clearing",
    evaluate: "failed_evaluating",
    prepare: "failed_preparing",
    "apply-prefix": "failed_prefix",
  }[context.failureOperation];
  const keyState = (state === "failed" && failureKeyState ? failureKeyState : state)
    .replace("-", "_");
  const needsEffectVariant = QUERY_STATUS_EFFECT_VARIANT_STATES.has(state);
  return mapText(
    `query.status.${keyState}${needsEffectVariant ? `.${suffix}` : ""}`,
    {
      source: context.appliedSource || "",
      pendingSource: context.pendingSource || "",
    },
  );
}

function queryDraftStatusIndicatorText(state) {
  const indicator = queryStatusIndicator(state);
  return mapText(`query.status_indicator.${indicator}`);
}

function pendingActivationState(canonicalDsl, afterApplyState) {
  if (afterApplyState === "invalid") {
    return "invalid-clearing";
  }
  return canonicalDsl ? "applying" : "clearing";
}

function queryActivationOperation(canonicalDsl, afterApplyState, source, draftSource) {
  if (!canonicalDsl || afterApplyState === "invalid") {
    return "clear";
  }
  return isAppliedSourcePrefix(source, draftSource) ? "apply-prefix" : "apply-current";
}

function isAppliedSourcePrefix(appliedSource, draftSource) {
  const applied = String(appliedSource ?? "");
  const draft = String(draftSource ?? "");
  if (!applied || applied === draft || !draft.startsWith(applied)) {
    return false;
  }
  const nextCharacter = draft.charAt(applied.length);
  const previousCharacter = applied.charAt(applied.length - 1);
  return /\s/.test(nextCharacter) ||
    /[()!"']/.test(nextCharacter) ||
    /[)\]"']/.test(previousCharacter);
}

export function deriveQueryInputDraftState(bundle, draftText, caretIndex, options = {}) {
  const source = normalizeQuerySourceText(draftText);
  const normalizedCaretIndex = normalizeQuerySourceCaretIndex(draftText, caretIndex);
  const editing = options.editing === true;
  const includeSuggestions = editing && options.suggestions !== false;
  const result = buildQueryInputDraftResultFromBundle(bundle, source);
  return {
    source,
    result,
    suggestions: includeSuggestions
      ? buildQueryInputSuggestions(bundle, source, normalizedCaretIndex)
      : [],
  };
}

function normalizedVisibleSuggestionCount(search, total) {
  const parsed = Number.parseInt(search?.suggestionVisibleCount, 10);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return Math.min(total, QUERY_SUGGESTION_PAGE_SIZE);
  }
  return Math.min(total, Math.max(QUERY_SUGGESTION_PAGE_SIZE, parsed));
}

function sameAcknowledgedExpressionProjection(left, right) {
  return Boolean(left) &&
    left.selectionElement === right.selectionElement &&
    left.languagePresentationRevision === right.languagePresentationRevision &&
    left.acknowledgedDsl === right.acknowledgedDsl &&
    left.acknowledgedExpression === right.acknowledgedExpression &&
    left.acknowledgedRawExpressionTree === right.acknowledgedRawExpressionTree &&
    left.appliedSource === right.appliedSource &&
    left.queryTextRelation === right.queryTextRelation &&
    left.fishCatalog === right.fishCatalog &&
    left.itemCatalog === right.itemCatalog &&
    left.patchCatalog === right.patchCatalog &&
    left.semanticTermCatalog === right.semanticTermCatalog &&
    left.tradeNpcCatalog === right.tradeNpcCatalog &&
    left.zoneCatalog === right.zoneCatalog;
}

export class FishyMapQueryInputElement extends HTMLElementBase {
  static get observedAttributes() {
    return ["data-search-state", "data-query-ribbon-visible"];
  }

  constructor() {
    super();
    this._shell = null;
    this._mapShellProjection = null;
    this._appliedMapShellProjection = null;
    this._elements = null;
    this._zoneCatalog = [];
    this._tradeNpcCatalog = null;
    this._tradeNpcCatalogStatus = "idle";
    this._tradeNpcCatalogActivationHydrated = false;
    this._draftText = "";
    this._suggestions = [];
    this._highlightedSuggestionIndex = -1;
    this._lastActiveDsl = "";
    this._acknowledgedDsl = "";
    this._acknowledgedExpression = null;
    this._acknowledgedRawExpressionTree = null;
    this._appliedSource = "";
    this._pendingActivation = null;
    this._failedActivation = null;
    this._activationRevision = 0;
    this._applicationState = "empty";
    this._editing = false;
    this._composing = false;
    this._dispatchingActivationRequest = false;
    this._deriveRevision = 0;
    this._lastDerivedRevision = -1;
    this._lastDraftState = null;
    this._deriveQueued = false;
    this._renderQueued = false;
    this._pendingCaretOffset = null;
    this._renderedSuggestionKey = "";
    this._suggestionVisibleCount = QUERY_SUGGESTION_PAGE_SIZE;
    this._catalogProjector = createSearchCatalogProjector();
    this._languagePresentationRevision = 0;
    this._lastExpressionProjection = null;
    this._ribbonLayoutPending = false;
    this._presentationFrameHandle = null;
    this._presentationFrameKind = "";
    this._presentationFrameRevision = 0;
    this._revealCaretOnNextPresentation = false;
    this._lastCatalogPreparation = null;
    this._queryScrollController = null;
    this._queryRibbonFontSet = null;
    this._catalogPreparation = createSearchCatalogPreparationOwner({
      primePreparedValues: (bundle, prepared) => {
        const transactions = [
          createQueryInputCatalogPreparedValuesTransaction(bundle, prepared),
          createSearchExpressionCatalogPreparedValuesTransaction(
            bundle.state?.catalog,
            prepared,
          ),
        ];
        const committed = [];
        try {
          for (const transaction of transactions) {
            transaction.commit();
            committed.push(transaction);
          }
        } catch (error) {
          committed.reverse().forEach((transaction) => transaction.rollback());
          throw error;
        }
        return () => {
          committed.reverse().forEach((transaction) => transaction.rollback());
        };
      },
      finishPreparation: (bundle) => prepareQueryInputCatalogIndexes(bundle),
      onPrepared: (preparation) => {
        this._lastCatalogPreparation = preparation;
        scheduleMicrotask(() => {
          if (this.isConnected === false || !this._catalogPreparation.ready) {
            return;
          }
          this.invalidateDerivedDraftState();
          if (this._editing && !this._composing && this._elements?.draftInput) {
            this.scheduleDraftDerivation(draftInputCaretOffset(this._elements.draftInput));
          }
          this.scheduleRender();
        });
      },
      onFailed: (error) => {
        if (this.isConnected === false) {
          return;
        }
        this.clearSuggestionState();
        this._applicationState = "failed";
        this.dataset.draftState = "failed";
        console.error("Map search catalog preparation failed", error);
        this.scheduleRender();
      },
    });
    this._handleZoneCatalogReady = (event) => {
      this._zoneCatalog = Array.isArray(event?.detail?.zoneCatalog) ? event.detail.zoneCatalog : [];
      if (this._elements) {
        this._elements.zoneCatalog = this._zoneCatalog;
      }
      this.invalidateDerivedDraftState();
      const catalogReady = this.prepareCatalogIndexes();
      if (catalogReady && this._editing && !this._composing && this._elements?.draftInput) {
        this.scheduleDraftDerivation(draftInputCaretOffset(this._elements.draftInput));
      }
      this.scheduleRender();
    };
    this._handleTradeNpcCatalogChanged = (event) => {
      this._tradeNpcCatalogStatus = String(event?.detail?.status ?? "idle")
        .trim()
        .toLowerCase();
      this._tradeNpcCatalog = isPlainObject(event?.detail?.catalog)
        ? cloneJson(event.detail.catalog)
        : null;
      this.invalidateDerivedDraftState();
      const catalogReady = this.prepareCatalogIndexes();
      if (catalogReady && this._editing && !this._composing && this._elements?.draftInput) {
        this.scheduleDraftDerivation(draftInputCaretOffset(this._elements.draftInput));
      }
      this.applyLoadedTradeNpcCatalogActivation();
      this.scheduleRender();
    };
    this._handleLanguageChange = () => {
      this._titleQueryOwner?.refresh();
      this._languagePresentationRevision += 1;
      this.localizeInput();
      this.scheduleRender();
    };
    this._handleContestSelectionChange = () => {
      this._lastExpressionProjection = null;
      this.scheduleRender();
    };
    this._handleContestVotingClose = () => {
      if (this.isConnected) {
        this.querySelector("[data-query-contest-voting]")?.focus();
      }
    };
    this._handleQueryRibbonFontsSettled = () => {
      this._ribbonLayoutPending = true;
      this.scheduleRender();
    };
    this._handleBridgeInputReceiptIngressReady = (event) => {
      if (event?.detail?.ready === true) {
        this.activateDeferredBridgeInput();
      }
    };
    this._handleBridgeInputSubmitted = (event) => {
      const detail = event?.detail;
      const pending = this._pendingActivation;
      if (
        detail?.owner !== QUERY_INPUT_RECEIPT_OWNER ||
        !pending ||
        detail.revision !== pending.revision
      ) {
        return;
      }
      pending.inputSequence = Number.isSafeInteger(detail.inputSequence) && detail.inputSequence > 0
        ? detail.inputSequence
        : null;
      this.scheduleRender();
    };
    this._handleBridgeInputApplied = (event) => {
      const detail = event?.detail;
      const pending = this._pendingActivation;
      if (
        detail?.owner !== QUERY_INPUT_RECEIPT_OWNER ||
        !pending ||
        detail.revision !== pending.revision ||
        !Number.isSafeInteger(detail.inputSequence) ||
        detail.inputSequence <= 0 ||
        detail.inputSequence !== pending.inputSequence ||
        !Number.isSafeInteger(detail.appliedInputSequence) ||
        detail.appliedInputSequence < detail.inputSequence
      ) {
        return;
      }
      this.acknowledgePendingActivation(pending);
    };
    this._handleBridgeInputFailed = (event) => {
      const detail = event?.detail;
      const pending = this._pendingActivation;
      if (
        detail?.owner !== QUERY_INPUT_RECEIPT_OWNER ||
        !pending ||
        detail.revision !== pending.revision
      ) {
        return;
      }
      this._failedActivation = {
        operation: pending.operation,
        draftText: pending.draftText,
        source: pending.source,
        afterApplyState: pending.afterApplyState,
        result: {
          canApply: true,
          canonicalDsl: pending.canonicalDsl,
          expression: cloneJson(pending.expression),
          rawExpressionTree: cloneRawExpressionTree(pending.rawExpressionTree),
          issues: cloneExpressionIssues(pending.issues),
        },
      };
      this._pendingActivation = null;
      this._applicationState = "failed";
      this.dataset.draftState = "failed";
      console.error("Map query activation failed", detail.message || "Bridge input failed");
      this.scheduleRender();
    };
    this._handleFocusIn = (event) => {
      if (event?.target?.closest?.("fishy-fishing-contest, [data-query-contest-voting]")) {
        this._editing = false;
        this.clearSuggestionState();
        this.requestEditorState("idle");
        this.scheduleRender();
        return;
      }
      void this._titleQueryOwner?.ensure();
      this._editing = true;
      this.requestEditorState("editing");
      if (!this._composing && this._elements?.draftInput) {
        this.scheduleDraftDerivation(draftInputCaretOffset(this._elements.draftInput));
      }
      this.scheduleRender();
    };
    this._handleFocusOut = (event) => {
      if (event?.relatedTarget && this.contains?.(event.relatedTarget)) {
        return;
      }
      this._editing = false;
      this.clearSuggestionState();
      this.requestEditorState("idle");
      this.scheduleRender();
    };
    this._handleInput = (event) => {
      const draftInput = event.target?.closest?.("#fishymap-search");
      if (draftInput && this.contains(draftInput)) {
        this.handleDraftInput(draftInput, {
          composing: this._composing || event.isComposing === true,
        });
      }
    };
    this._handleCompositionStart = (event) => {
      if (event.target === this._elements?.draftInput) {
        this.handleDraftCompositionStart(event.target);
      }
    };
    this._handleCompositionEnd = (event) => {
      if (event.target === this._elements?.draftInput) {
        this.handleDraftCompositionEnd(event.target);
      }
    };
    this._handleKeyDown = (event) => {
      if (event.target === this._elements?.draftInput) {
        this.handleDraftKeyDown(event);
      }
    };
    this._handleSuggestionsScroll = () => {
      this.handleSuggestionsScroll();
    };
    this._handleClick = (event) => {
      const contestButton = event.target.closest("[data-query-contest-voting]");
      if (contestButton && this.contains(contestButton)) {
        event.preventDefault();
        this._elements.contest?.openVoting();
        return;
      }
      const clearButton = event.target.closest("#fishymap-query-clear");
      if (clearButton && this.contains(clearButton)) {
        event.preventDefault();
        this.clearQueryInput();
        return;
      }
      const suggestionButton = event.target.closest("button[data-query-suggestion-index]");
      if (suggestionButton && this.contains(suggestionButton)) {
        event.preventDefault();
        this.acceptSuggestion(Number.parseInt(suggestionButton.getAttribute("data-query-suggestion-index"), 10));
      }
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) {
      return;
    }
    if (name === "data-query-ribbon-visible") {
      if (newValue === "true") {
        this._ribbonLayoutPending = true;
        this.scheduleRender();
      }
      return;
    }
    if (name !== "data-search-state") {
      return;
    }
    this.scheduleRender();
  }

  get mapShellProjection() {
    return this._mapShellProjection;
  }

  set mapShellProjection(value) {
    const projection = assertMapShellProjectionV1(value);
    const currentRevision = this._mapShellProjection?.revision;
    if (currentRevision != null && projection.revision < currentRevision) {
      throw new RangeError(
        `Map shell projection revision ${projection.revision} regresses from ${currentRevision}`,
      );
    }
    if (currentRevision === projection.revision) {
      return;
    }
    this._mapShellProjection = projection;
    if (this._elements && this.isConnected !== false) {
      this.applyMapShellProjection();
    }
  }

  applyMapShellProjection() {
    const projection = this._mapShellProjection;
    if (!projection || this._appliedMapShellProjection?.revision === projection.revision) {
      return false;
    }
    const previous = this._appliedMapShellProjection;
    this._appliedMapShellProjection = projection;
    if (!projectionChangesQueryDerivation(previous, projection)) {
      return true;
    }
    if (expressionUsesTitles(projection.ui?.search?.expression)) void this._titleQueryOwner?.ensure();
    if (previous?.runtime?.catalogRevision !== projection.runtime?.catalogRevision) this._titleQueryOwner?.refresh();
    const nextActiveDsl = activeExpressionDslFromBundle(this.stateBundle());
    if (!this._dispatchingActivationRequest && nextActiveDsl !== this._lastActiveDsl) {
      this.syncDraftFromActive({ force: true, writeInput: true });
    } else if (!this._dispatchingActivationRequest) {
      this.invalidateDerivedDraftState();
    }
    const catalogReady = this.prepareCatalogIndexes();
    if (catalogReady && this._editing && !this._composing && this._elements?.draftInput) {
      this.scheduleDraftDerivation(draftInputCaretOffset(this._elements.draftInput));
    }
    this.applyLoadedTradeNpcCatalogActivation();
    this.scheduleRender();
    return true;
  }

  connectedCallback() {
    this._catalogProjector = createSearchCatalogProjector();
    this._appliedMapShellProjection = null;
    this._lastExpressionProjection = null;
    this._ribbonLayoutPending = true;
    this._shell = this.closest?.("#map-page-shell") || null;
    this._tradeNpcCatalog = isPlainObject(this._shell?.fishyMapTradeNpcCatalog)
      ? cloneJson(this._shell.fishyMapTradeNpcCatalog)
      : null;
    this._tradeNpcCatalogStatus = String(
      this._shell?.fishyMapTradeNpcCatalogStatus ?? "idle",
    ).trim().toLowerCase();
    if (Array.isArray(this._shell?.fishyMapZoneCatalog)) {
      this._zoneCatalog = this._shell.fishyMapZoneCatalog;
    }
    ensureQueryInputMarkup(this);
    this._elements = {
      searchWindow: this._shell?.querySelector?.("#fishymap-search-window") || null,
      querySurface: this.querySelector("#fishymap-query-surface"),
      queryEditor: this.querySelector("#fishymap-query-editor"),
      querySourceMeasure: this.querySelector("#fishymap-query-source-measure"),
      draftInput: this.querySelector("#fishymap-search"),
      clearButton: this.querySelector("#fishymap-query-clear"),
      queryStatus: this._shell?.querySelector?.("#fishymap-query-status") || null,
      searchSelectionShell: this.querySelector("#fishymap-query-selection-shell"),
      searchSelection: this.querySelector("#fishymap-query-selection"),
      suggestionsShell: this.querySelector("#fishymap-query-suggestions-shell"),
      suggestions: this.querySelector("#fishymap-query-suggestions"),
      contest: this.ownerDocument.getElementById("fishymap-query-contest"),
      zoneCatalog: this._zoneCatalog,
    };
    this.connectTitleQueries();
    this._queryScrollController?.dispose();
    this._queryScrollController = null;
    if (
      this._elements.searchSelectionShell &&
      this._elements.queryEditor &&
      this._elements.draftInput
    ) {
      this._queryScrollController = createQueryScrollController({
        host: this._elements.searchSelectionShell,
        scrollElement: this._elements.queryEditor,
        input: this._elements.draftInput,
        onCaretEdgeRevealRequested: () => {
          this.schedulePaintAlignedPresentation({ revealCaret: true });
        },
      });
    }
    this.addEventListener("focusin", this._handleFocusIn);
    this.addEventListener("focusout", this._handleFocusOut);
    this.addEventListener("input", this._handleInput);
    this.addEventListener("compositionstart", this._handleCompositionStart);
    this.addEventListener("compositionend", this._handleCompositionEnd);
    this.addEventListener("keydown", this._handleKeyDown);
    this.addEventListener("click", this._handleClick);
    this._elements.suggestions?.addEventListener?.("scroll", this._handleSuggestionsScroll);
    this._elements.contest?.addEventListener(FISHING_CONTEST_SELECTION_CHANGE_EVENT, this._handleContestSelectionChange);
    this._elements.contest?.addEventListener(FISHING_CONTEST_VOTING_CLOSE_EVENT, this._handleContestVotingClose);
    this._shell?.addEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.addEventListener?.(
      FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
      this._handleTradeNpcCatalogChanged,
    );
    globalThis.window?.addEventListener?.(
      globalThis.window?.__fishystuffLanguage?.event || "fishystuff:languagechange",
      this._handleLanguageChange,
    );
    this._queryRibbonFontSet = this.ownerDocument?.fonts || null;
    this._queryRibbonFontSet?.addEventListener?.("loadingdone", this._handleQueryRibbonFontsSettled);
    this._queryRibbonFontSet?.addEventListener?.("loadingerror", this._handleQueryRibbonFontsSettled);
    this._shell?.addEventListener?.(
      FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_EVENT,
      this._handleBridgeInputReceiptIngressReady,
    );
    this._shell?.addEventListener?.(
      FISHYMAP_BRIDGE_INPUT_SUBMITTED_EVENT,
      this._handleBridgeInputSubmitted,
    );
    this._shell?.addEventListener?.(
      FISHYMAP_BRIDGE_INPUT_APPLIED_EVENT,
      this._handleBridgeInputApplied,
    );
    this._shell?.addEventListener?.(
      FISHYMAP_BRIDGE_INPUT_FAILED_EVENT,
      this._handleBridgeInputFailed,
    );
    this.localizeInput();
    if (!this.applyMapShellProjection()) {
      this.syncDraftFromActive({ force: true, writeInput: true });
      this.prepareCatalogIndexes();
      this.applyLoadedTradeNpcCatalogActivation();
      this.scheduleRender();
    }
  }

  disconnectedCallback() {
    this._titleQueryOwner?.dispose();
    this._titleQueryOwner = null;
    this._queryScrollController?.dispose();
    this._queryScrollController = null;
    this.removeEventListener("focusin", this._handleFocusIn);
    this.removeEventListener("focusout", this._handleFocusOut);
    this.removeEventListener("input", this._handleInput);
    this.removeEventListener("compositionstart", this._handleCompositionStart);
    this.removeEventListener("compositionend", this._handleCompositionEnd);
    this.removeEventListener("keydown", this._handleKeyDown);
    this.removeEventListener("click", this._handleClick);
    this._elements?.suggestions?.removeEventListener?.("scroll", this._handleSuggestionsScroll);
    this._elements?.contest?.removeEventListener(FISHING_CONTEST_SELECTION_CHANGE_EVENT, this._handleContestSelectionChange);
    this._elements?.contest?.removeEventListener(FISHING_CONTEST_VOTING_CLOSE_EVENT, this._handleContestVotingClose);
    this._shell?.removeEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.removeEventListener?.(
      FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
      this._handleTradeNpcCatalogChanged,
    );
    globalThis.window?.removeEventListener?.(
      globalThis.window?.__fishystuffLanguage?.event || "fishystuff:languagechange",
      this._handleLanguageChange,
    );
    this._queryRibbonFontSet?.removeEventListener?.("loadingdone", this._handleQueryRibbonFontsSettled);
    this._queryRibbonFontSet?.removeEventListener?.("loadingerror", this._handleQueryRibbonFontsSettled);
    this._queryRibbonFontSet = null;
    this._shell?.removeEventListener?.(
      FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_EVENT,
      this._handleBridgeInputReceiptIngressReady,
    );
    this._shell?.removeEventListener?.(
      FISHYMAP_BRIDGE_INPUT_SUBMITTED_EVENT,
      this._handleBridgeInputSubmitted,
    );
    this._shell?.removeEventListener?.(
      FISHYMAP_BRIDGE_INPUT_APPLIED_EVENT,
      this._handleBridgeInputApplied,
    );
    this._shell?.removeEventListener?.(
      FISHYMAP_BRIDGE_INPUT_FAILED_EVENT,
      this._handleBridgeInputFailed,
    );
    this._deriveRevision += 1;
    this._deriveQueued = false;
    this._renderQueued = false;
    this.cancelPaintAlignedPresentation();
    this._pendingActivation = null;
    this._lastExpressionProjection = null;
    this._ribbonLayoutPending = false;
    this._catalogPreparation.dispose();
  }

  searchPanelProjectionInput() {
    return searchPanelSignalsFromProjection(this._mapShellProjection);
  }

  localizeInput() {
    const input = this._elements?.draftInput;
    if (input) {
      input.placeholder = mapText("search.placeholder");
      input.setAttribute?.("aria-label", mapText("query.input_label"));
    }
    const clearButton = this._elements?.clearButton;
    if (clearButton) {
      const clearLabel = mapText("query.clear");
      clearButton.setAttribute?.("aria-label", clearLabel);
      clearButton.setAttribute?.("data-fishy-tooltip", clearLabel);
    }
  }

  requestEditorState(state) {
    return dispatchMapQueryEditorStateRequest(this, state);
  }

  requestQueryActivation(expression, { revision, editorState = "editing" } = {}) {
    return dispatchMapQueryActivationRequest(this, {
      activationRevision: revision,
      expression,
      editorState,
    });
  }

  requestRestoredQueryEffects() {
    return dispatchMapQueryRestoredEffectsRequest(this);
  }

  applyLoadedTradeNpcCatalogActivation() {
    if (
      this._tradeNpcCatalogActivationHydrated ||
      this._tradeNpcCatalogStatus !== "loaded" ||
      !isPlainObject(this._tradeNpcCatalog)
    ) {
      return false;
    }
    if (!this._mapShellProjection) {
      return false;
    }
    this._tradeNpcCatalogActivationHydrated = true;
    try {
      const dispatched = this.requestRestoredQueryEffects();
      if (dispatched === false) {
        this._tradeNpcCatalogActivationHydrated = false;
        return false;
      }
      return true;
    } catch (error) {
      this._tradeNpcCatalogActivationHydrated = false;
      throw error;
    }
  }

  connectTitleQueries() {
    this._titleQueryOwner?.dispose();
    this._titleQueryOwner = createMapTitleQueryOwner({
      repository: browserFishydexStateComposition().current,
      resolveApiUrl: path => globalThis.window.__fishystuffResolveApiUrl(path),
      readLanguage: currentDataLanguage,
      readFishCatalog: () => this._mapShellProjection?.runtime?.catalog?.fish ?? null,
      guideCountLookup: progress => globalThis.window.__fishystuffSharedFishState.createFishGuideCountLookup(progress),
      onChange: () => {
        this.invalidateDerivedDraftState();
        this._lastExpressionProjection = null;
        if (expressionUsesTitles(this._acknowledgedExpression)) {
          const result = buildQueryInputDraftResultFromBundle(this.stateBundle(), this._appliedSource);
          if (result.canApply) {
            this._acknowledgedRawExpressionTree = cloneRawExpressionTree(result.rawExpressionTree);
          }
        }
        if (this._editing && !this._composing && this._elements?.draftInput) {
          this.scheduleDraftDerivation(draftInputCaretOffset(this._elements.draftInput));
        }
        this.scheduleRender();
        this.dispatchEvent(new CustomEvent(TITLE_QUERY_CHANGED, { bubbles: true }));
      },
    });
  }

  get titleQueryProjection() { return this._titleQueryOwner?.projection || { kind: "idle" }; }

  stateBundle() {
    return buildSearchPanelStateBundle(this.searchPanelProjectionInput(), {
      titleProjection: this.titleQueryProjection,
      catalogProjector: this._catalogProjector,
      tradeNpcCatalog: this._tradeNpcCatalog,
      zoneCatalog: this._zoneCatalog,
    });
  }

  prepareCatalogIndexes() {
    if (this.isConnected === false) {
      return false;
    }
    return this._catalogPreparation.prepare(this.stateBundle());
  }

  invalidateDerivedDraftState() {
    this._deriveRevision += 1;
    this._lastDraftState = null;
  }

  scheduleDraftDerivation(caretOffset) {
    if (!this._editing || this._composing) {
      return;
    }
    this._pendingCaretOffset = Number.isInteger(caretOffset) ? caretOffset : this._draftText.length;
    this._deriveRevision += 1;
    if (!this._catalogPreparation.ready) {
      if (this._catalogPreparation.failed) {
        this.clearSuggestionState();
        this._applicationState = "failed";
        this.dataset.draftState = "failed";
        this.scheduleRender();
      }
      return;
    }
    if (this._deriveQueued) {
      return;
    }
    this._deriveQueued = true;
    scheduleMicrotask(() => {
      this._deriveQueued = false;
      const revision = this._deriveRevision;
      const pendingCaretOffset = Number.isInteger(this._pendingCaretOffset)
        ? this._pendingCaretOffset
        : this._draftText.length;
      this._pendingCaretOffset = null;
      this.runDraftDerivation(revision, pendingCaretOffset);
    });
  }

  runDraftDerivation(revision, caretOffset) {
    if (
      revision !== this._deriveRevision ||
      revision === this._lastDerivedRevision ||
      this.isConnected === false ||
      !this._editing ||
      this._composing ||
      !this._catalogPreparation.ready
    ) {
      return;
    }
    const bundle = this.stateBundle();
    const source = this._draftText;
    let draftState;
    try {
      draftState = deriveQueryInputDraftState(bundle, source, caretOffset, {
        editing: true,
        suggestions: true,
      });
    } catch (error) {
      if (revision !== this._deriveRevision || source !== this._draftText) {
        return;
      }
      this._lastDerivedRevision = revision;
      this._lastDraftState = null;
      this.clearSuggestionState();
      this._failedActivation = {
        operation: "evaluate",
        draftText: source,
        source,
      };
      this._applicationState = "failed";
      this.dataset.draftState = "failed";
      console.error("Map query derivation failed", error);
      this.scheduleRender();
      return;
    }
    if (revision !== this._deriveRevision || source !== this._draftText || !this._editing) {
      return;
    }
    this._lastDerivedRevision = revision;
    this._lastDraftState = draftState;
    this.applyDerivedDraftState(draftState, bundle);
  }

  syncDraftFromActive(options = {}) {
    const bundle = this.stateBundle();
    const activeDsl = activeExpressionDslFromBundle(bundle);
    const force = options.force === true;
    if (force || activeDsl !== this._lastActiveDsl) {
      this._lastActiveDsl = activeDsl;
      this._acknowledgedDsl = activeDsl;
      this._acknowledgedExpression = activeExpressionFromBundle(bundle);
      const activeResult = buildQueryInputDraftResultFromBundle(bundle, activeDsl);
      this._acknowledgedRawExpressionTree = cloneRawExpressionTree(
        activeResult.rawExpressionTree,
      );
      this._appliedSource = activeDsl;
      this._pendingActivation = null;
      this._failedActivation = null;
      this._draftText = activeDsl;
      this.clearSuggestionState();
      this.invalidateDerivedDraftState();
      this._applicationState = activeDsl ? "applied" : "empty";
      this.dataset.draftState = this._applicationState;
      if (options.writeInput === true && this._elements?.draftInput) {
        setDraftInputValue(this._elements.draftInput, activeDsl, activeDsl.length, {
          restoreCaret: true,
        });
      }
    }
  }

  handleDraftInput(input, options = {}) {
    this._editing = true;
    this._draftText = draftInputSource(input);
    setDraftInputEmptyState(input, this._draftText);
    this.invalidateDerivedDraftState();
    this.clearSuggestionState();
    this._failedActivation = null;
    if (options.composing === true) {
      this._applicationState = "composing";
      this.dataset.draftState = "composing";
      this.scheduleRender();
      return;
    }
    this._applicationState = "evaluating";
    this.dataset.draftState = "evaluating";
    const caretOffset = draftInputCaretOffset(input);
    this.scheduleDraftDerivation(caretOffset);
    this.scheduleRender();
  }

  handleDraftCompositionStart(input) {
    this._composing = true;
    this.handleDraftInput(input, { composing: true });
  }

  handleDraftCompositionEnd(input) {
    this._composing = false;
    this.handleDraftInput(input);
  }

  clearQueryInput() {
    const input = this._elements?.draftInput;
    if (!input || !draftInputSource(input)) {
      return false;
    }
    this._composing = false;
    setDraftInputValue(input, "", 0, { restoreCaret: true });
    this.handleDraftInput(input);
    input.focus?.();
    return true;
  }

  handleDraftKeyDown(event) {
    if (event.isComposing || this._composing) {
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      this.revertDraftToActive();
      return;
    }
    if (event.altKey && event.key === "ArrowDown" && this._suggestions.length) {
      event.preventDefault();
      this._highlightedSuggestionIndex = Math.min(
        this._suggestions.length - 1,
        this._highlightedSuggestionIndex + 1,
      );
      this.ensureHighlightedSuggestionVisible();
      this.scheduleRender();
      return;
    }
    if (event.altKey && event.key === "ArrowUp" && this._suggestions.length) {
      event.preventDefault();
      this._highlightedSuggestionIndex = Math.max(0, this._highlightedSuggestionIndex - 1);
      this.ensureHighlightedSuggestionVisible();
      this.scheduleRender();
      return;
    }
    if (event.key === "Enter" && this._suggestions.length) {
      if (this._highlightedSuggestionIndex >= 0 && this._suggestions[this._highlightedSuggestionIndex]) {
        event.preventDefault();
        this.acceptSuggestion(this._highlightedSuggestionIndex);
        return;
      }
    }
    if (event.key === "Enter") {
      event.preventDefault();
      this.applyDraftNow();
    }
  }

  retryFailedActivation() {
    const failed = this._failedActivation;
    if (!failed?.result || failed.draftText !== this._draftText) {
      return false;
    }
    const operation = failed.operation;
    const activationState = this.commitDraftResult(
      this.stateBundle(),
      failed.result,
      failed.source,
      {
        afterApplyState: failed.afterApplyState,
        resubmitFailedBridgeInput: true,
      },
    );
    this._applicationState = operation === "apply-prefix" && activationState === "applying"
      ? "partial-applying"
      : activationState;
    this.dataset.draftState = this._applicationState;
    this.scheduleRender();
    return this._applicationState !== "failed";
  }

  applyDraftNow() {
    if (this._composing) {
      return false;
    }
    const input = this._elements?.draftInput;
    if (input) {
      this._draftText = draftInputSource(input);
    }
    if (
      this._applicationState === "failed" &&
      this._failedActivation?.result &&
      this._failedActivation.draftText === this._draftText
    ) {
      return this.retryFailedActivation();
    }
    const caretOffset = input ? draftInputCaretOffset(input) : this._draftText.length;
    if (
      this._lastDerivedRevision === this._deriveRevision &&
      this._lastDraftState?.source === normalizeQuerySourceText(this._draftText)
    ) {
      if (
        this._applicationState === "failed" &&
        this._lastDraftState.result.canApply === true
      ) {
        this.applyDerivedDraftState(this._lastDraftState, this.stateBundle());
      }
      return this._lastDraftState.result.canApply === true;
    }
    this.scheduleDraftDerivation(caretOffset);
    return false;
  }

  revertDraftToActive() {
    this._editing = false;
    this._composing = false;
    this.clearSuggestionState();
    this._draftText = this._acknowledgedDsl;
    if (this._elements?.draftInput) {
      setDraftInputValue(
        this._elements.draftInput,
        this._draftText,
        this._draftText.length,
        { restoreCaret: true },
      );
    }
    const bundle = this.stateBundle();
    const result = buildQueryInputDraftResultFromBundle(bundle, this._draftText);
    const previousActivationRevision = this._activationRevision;
    const activationState = this.commitDraftResult(
      bundle,
      result,
      this._draftText,
      { editorState: "idle" },
    );
    this._applicationState = activationState === "failed"
      ? "failed"
      : activationState;
    this.dataset.draftState = this._applicationState;
    if (this._activationRevision === previousActivationRevision) {
      this.requestEditorState("idle");
    }
    this.scheduleRender();
  }

  setDerivedSuggestions(suggestions) {
    const nextSuggestions = Array.isArray(suggestions) ? suggestions : [];
    const listChanged = querySuggestionListKey(this._suggestions) !== querySuggestionListKey(nextSuggestions);
    this._suggestions = nextSuggestions;
    this._suggestionVisibleCount = listChanged
      ? Math.min(this._suggestions.length, QUERY_SUGGESTION_PAGE_SIZE)
      : normalizedVisibleSuggestionCount(
          { suggestionVisibleCount: this._suggestionVisibleCount },
          this._suggestions.length,
        );
    if (!this._suggestions.length) {
      this._highlightedSuggestionIndex = -1;
      return;
    }
    if (this._highlightedSuggestionIndex >= this._suggestions.length) {
      this._highlightedSuggestionIndex = this._suggestions.length - 1;
    }
  }

  clearSuggestionState() {
    this._suggestions = [];
    this._highlightedSuggestionIndex = -1;
    this._suggestionVisibleCount = QUERY_SUGGESTION_PAGE_SIZE;
  }

  ensureHighlightedSuggestionVisible() {
    if (this._highlightedSuggestionIndex < 0) {
      return;
    }
    const total = this._suggestions.length;
    const visibleCount = normalizedVisibleSuggestionCount(
      { suggestionVisibleCount: this._suggestionVisibleCount },
      total,
    );
    if (this._highlightedSuggestionIndex < visibleCount) {
      return;
    }
    const nextVisibleCount = Math.min(
      total,
      Math.max(visibleCount + QUERY_SUGGESTION_PAGE_SIZE, this._highlightedSuggestionIndex + 1),
    );
    this._suggestionVisibleCount = nextVisibleCount;
  }

  handleSuggestionsScroll() {
    const list = this._elements?.suggestions;
    if (!list || !this._editing || !this._suggestions.length) {
      return;
    }
    const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (remaining > QUERY_SUGGESTION_SCROLL_THRESHOLD_PX) {
      return;
    }
    const visibleCount = normalizedVisibleSuggestionCount(
      { suggestionVisibleCount: this._suggestionVisibleCount },
      this._suggestions.length,
    );
    if (visibleCount >= this._suggestions.length) {
      return;
    }
    const nextVisibleCount = Math.min(this._suggestions.length, visibleCount + QUERY_SUGGESTION_PAGE_SIZE);
    this._suggestionVisibleCount = nextVisibleCount;
  }

  acceptSuggestion(index) {
    const suggestion = this._suggestions[index];
    if (!suggestion) {
      return false;
    }
    const next = applyQueryInputSuggestionToDraft(this._draftText, suggestion);
    this._draftText = next.draftText;
    this._pendingCaretOffset = next.caretIndex;
    this._editing = true;
    this.invalidateDerivedDraftState();
    this._applicationState = "evaluating";
    this.dataset.draftState = "evaluating";
    this.clearSuggestionState();
    if (this._elements?.draftInput) {
      this._elements.draftInput.focus?.();
      setDraftInputValue(this._elements.draftInput, this._draftText, next.caretIndex, {
        restoreCaret: true,
      });
    }
    this.scheduleDraftDerivation(next.caretIndex);
    return true;
  }

  renderSuggestions(bundle) {
    const suggestions = this._editing ? this._suggestions : [];
    const visibleCount = this._editing
      ? normalizedVisibleSuggestionCount(
          { suggestionVisibleCount: this._suggestionVisibleCount },
          suggestions.length,
        )
      : 0;
    this._suggestionVisibleCount = visibleCount;
    const visibleSuggestions = suggestions.slice(0, visibleCount);
    const renderKey = JSON.stringify([
      querySuggestionListKey(visibleSuggestions),
      this._highlightedSuggestionIndex,
      suggestions.length,
      visibleCount,
    ]);
    const suggestionsShell = this._elements?.suggestionsShell;
    const suggestionsElement = this._elements?.suggestions;
    if (!suggestionsShell || !suggestionsElement) {
      return;
    }
    const hasSuggestions = suggestions.length > 0;
    suggestionsShell.hidden = !hasSuggestions;
    if (!hasSuggestions) {
      suggestionsElement.innerHTML = "";
      this._renderedSuggestionKey = renderKey;
      return;
    }
    if (this._renderedSuggestionKey === renderKey) {
      return;
    }
    this._renderedSuggestionKey = renderKey;
    suggestionsElement.innerHTML = buildQuerySuggestionRowsHtml(
      visibleSuggestions,
      bundle,
      this._highlightedSuggestionIndex,
    );
  }

  renderDraftInput() {
    const input = this._elements?.draftInput;
    if (!input) {
      return false;
    }
    if (!this._editing) {
      setDraftInputValue(input, this._draftText, this._draftText.length, {
        restoreCaret: false,
      });
    }
    setDraftInputEmptyState(input, this._draftText);
    const visibleSource = this._editing ? draftInputSource(input) : this._draftText;
    setBooleanProperty(this._elements?.clearButton, "hidden", !visibleSource);
    const measuredSource = visibleSource || " ";
    const sourceMeasure = this._elements?.querySourceMeasure;
    const sourceChanged = sourceMeasure?.textContent !== measuredSource;
    setTextContent(sourceMeasure, measuredSource);
    return sourceChanged;
  }

  tracksBridgeInputReceipts() {
    return bridgeInputReceiptIngressIsReady(this._shell);
  }

  waitsForBridgeInputReceiptIngress() {
    return Boolean(this._shell) && !this.tracksBridgeInputReceipts();
  }

  activateDeferredBridgeInput() {
    const deferred = this._pendingActivation;
    if (!deferred?.deferred || !this.tracksBridgeInputReceipts()) {
      return false;
    }
    if (this._applicationState === "evaluating" || this._applicationState === "composing") {
      this._pendingActivation = null;
      return false;
    }
    const keepPartialState = this._applicationState === "partial-applying";
    this._pendingActivation = null;
    const activationState = this.commitDraftResult(
      this.stateBundle(),
      {
        canApply: true,
        canonicalDsl: deferred.canonicalDsl,
        expression: deferred.expression,
        rawExpressionTree: deferred.rawExpressionTree,
        issues: deferred.issues,
      },
      deferred.source,
      {
        afterApplyState: deferred.afterApplyState,
        editorState: deferred.editorState,
      },
    );
    this._applicationState = keepPartialState && activationState === "applying"
      ? "partial-applying"
      : activationState;
    this.dataset.draftState = this._applicationState;
    this.scheduleRender();
    return activationState !== "failed";
  }

  acknowledgePendingActivation(pending) {
    if (!pending || this._pendingActivation?.revision !== pending.revision) {
      return false;
    }
    this._acknowledgedDsl = pending.canonicalDsl;
    this._acknowledgedExpression = cloneJson(pending.expression);
    this._acknowledgedRawExpressionTree = cloneRawExpressionTree(pending.rawExpressionTree);
    this._appliedSource = pending.source;
    this._pendingActivation = null;
    this._failedActivation = null;
    const settledStates = {
      applying: pending.canonicalDsl ? "applied" : "empty",
      clearing: "empty",
      "partial-applying": "partial",
      "invalid-clearing": "invalid",
    };
    this._applicationState = settledStates[this._applicationState] || this._applicationState;
    this.dataset.draftState = this._applicationState;
    this.scheduleRender();
    return true;
  }

  commitDraftResult(bundle, result, source, options = {}) {
    if (!result?.canApply) {
      return "failed";
    }
    this._failedActivation = null;
    const editorState = options.editorState === "idle" ? "idle" : "editing";
    const activationExpression = resolveSearchExpression(result.expression);
    let requiresActivation = result.canonicalDsl !== activeExpressionDslFromBundle(bundle);
    const activationSource = result.canonicalDsl ? String(source || "") : "";
    if (!requiresActivation && options.resubmitFailedBridgeInput === true) {
      requiresActivation = true;
    }
    const pending = this._pendingActivation;
    if (!requiresActivation && pending?.canonicalDsl === result.canonicalDsl) {
      pending.source = activationSource;
      pending.expression = cloneJson(activationExpression);
      pending.rawExpressionTree = cloneRawExpressionTree(result.rawExpressionTree);
      if (Object.prototype.hasOwnProperty.call(result, "issues")) {
        pending.issues = cloneExpressionIssues(result.issues);
      }
      pending.afterApplyState = options.afterApplyState;
      if (pending.deferred) {
        pending.editorState = editorState;
      }
      return pendingActivationState(pending.canonicalDsl, options.afterApplyState);
    }
    if (
      !requiresActivation &&
      result.canonicalDsl === this._acknowledgedDsl &&
      this._pendingActivation?.deferred
    ) {
      this._pendingActivation = null;
      this._appliedSource = activationSource;
      this._acknowledgedRawExpressionTree = cloneRawExpressionTree(result.rawExpressionTree);
      return options.afterApplyState === "invalid"
        ? "invalid"
        : result.canonicalDsl
          ? "applied"
          : "empty";
    }
    if (
      !requiresActivation &&
      result.canonicalDsl === this._acknowledgedDsl &&
      !this._pendingActivation
    ) {
      this._appliedSource = activationSource;
      this._acknowledgedRawExpressionTree = cloneRawExpressionTree(result.rawExpressionTree);
      return result.canonicalDsl ? "applied" : "empty";
    }
    if (!requiresActivation) {
      this._acknowledgedDsl = result.canonicalDsl;
      this._acknowledgedExpression = cloneJson(result.expression);
      this._acknowledgedRawExpressionTree = cloneRawExpressionTree(result.rawExpressionTree);
      this._appliedSource = activationSource;
      return options.afterApplyState === "invalid"
        ? "invalid"
        : result.canonicalDsl
          ? "applied"
          : "empty";
    }
    const tracksReceipt = this.tracksBridgeInputReceipts();
    const waitsForReceiptIngress = this.waitsForBridgeInputReceiptIngress();
    const revision = ++this._activationRevision;
    const nextPending = {
      revision,
      canonicalDsl: result.canonicalDsl,
      expression: cloneJson(activationExpression),
      rawExpressionTree: cloneRawExpressionTree(result.rawExpressionTree),
      issues: cloneExpressionIssues(result.issues),
      source: activationSource,
      draftText: this._draftText,
      operation: queryActivationOperation(
        result.canonicalDsl,
        options.afterApplyState,
        activationSource,
        this._draftText,
      ),
      inputSequence: null,
      afterApplyState: options.afterApplyState,
      editorState,
      deferred: waitsForReceiptIngress,
    };
    if (tracksReceipt || waitsForReceiptIngress) {
      this._pendingActivation = nextPending;
    }
    if (waitsForReceiptIngress) {
      return pendingActivationState(result.canonicalDsl, options.afterApplyState);
    }
    try {
      this._dispatchingActivationRequest = true;
      const dispatched = this.requestQueryActivation(
        activationExpression,
        {
          revision,
          editorState,
        },
      );
      if (dispatched === false) {
        throw new Error("Map query activation request was not dispatched");
      }
      this._lastActiveDsl = result.canonicalDsl;
      if (tracksReceipt) {
        if (this._pendingActivation?.revision !== revision) {
          this._lastActiveDsl = activeExpressionDslFromBundle(this.stateBundle());
          return "failed";
        }
        return pendingActivationState(result.canonicalDsl, options.afterApplyState);
      }
      this._acknowledgedDsl = result.canonicalDsl;
      this._acknowledgedExpression = cloneJson(result.expression);
      this._acknowledgedRawExpressionTree = cloneRawExpressionTree(result.rawExpressionTree);
      this._appliedSource = activationSource;
      return options.afterApplyState === "invalid"
        ? "invalid"
        : result.canonicalDsl
          ? "applied"
          : "empty";
    } catch (error) {
      console.error("Map query activation failed", error);
      this._failedActivation = {
        operation: nextPending.operation,
        draftText: nextPending.draftText,
        source: nextPending.source,
        afterApplyState: nextPending.afterApplyState,
        result: {
          canApply: true,
          canonicalDsl: nextPending.canonicalDsl,
          expression: cloneJson(nextPending.expression),
          rawExpressionTree: cloneRawExpressionTree(nextPending.rawExpressionTree),
          issues: cloneExpressionIssues(nextPending.issues),
        },
      };
      if (this._pendingActivation?.revision === revision) {
        this._pendingActivation = null;
      }
      if (tracksReceipt) {
        this._lastActiveDsl = activeExpressionDslFromBundle(this.stateBundle());
      }
      return "failed";
    } finally {
      this._dispatchingActivationRequest = false;
    }
  }

  applyDerivedDraftState(draftState, bundle) {
    this.setDerivedSuggestions(draftState.suggestions);
    const result = draftState.result;
    if (!result.canApply) {
      if (result.validPrefix?.expression && result.validPrefix.canonicalDsl) {
        const activationState = this.commitDraftResult(
          bundle,
          {
            canApply: true,
            canonicalDsl: result.validPrefix.canonicalDsl,
            expression: result.validPrefix.expression,
            rawExpressionTree: result.validPrefix.rawExpressionTree,
            issues: result.validPrefix.issues,
          },
          result.validPrefix.sourceText,
        );
        this._applicationState = activationState === "applying"
          ? "partial-applying"
          : activationState === "applied"
            ? "partial"
            : activationState;
        this.dataset.draftState = this._applicationState;
        this.scheduleRender();
        return;
      }
      if (this._pendingActivation?.canonicalDsl || this._acknowledgedDsl) {
        const emptyResult = buildQueryInputDraftResultFromBundle(bundle, "");
        const activationState = this.commitDraftResult(bundle, emptyResult, "", {
          afterApplyState: "invalid",
        });
        if (activationState === "failed") {
          this._applicationState = "failed";
          this.dataset.draftState = "failed";
          this.scheduleRender();
          return;
        }
        this._applicationState = activationState;
        this.dataset.draftState = activationState;
        this.scheduleRender();
        return;
      }
      this._applicationState = "invalid";
      this.dataset.draftState = this._applicationState;
      this.scheduleRender();
      return;
    }

    this._applicationState = this.commitDraftResult(bundle, result, draftState.source);
    this.dataset.draftState = this._applicationState;
    this.scheduleRender();
  }

  renderStatus(bundle) {
    const status = this._elements?.queryStatus;
    if (!status) {
      return;
    }
    const hasAppliedExpression = Boolean(this._acknowledgedDsl);
    status.dataset.state = this._applicationState;
    const detailedText = queryDraftStatusText(this._applicationState, {
      hasAppliedExpression,
      appliedSource: this._appliedSource,
      pendingSource: this._pendingActivation?.source || this._failedActivation?.source || "",
      failureOperation: this._catalogPreparation.failed
        ? "prepare"
        : this._failedActivation?.operation || "",
    });
    const indicatorText = queryDraftStatusIndicatorText(this._applicationState);
    if (!status.querySelector('.fishymap-query-status-label')) status.innerHTML = queryStatusMarkup("fishymap-query-status-detail");
    renderQueryStatus(status, this._elements?.querySurface, {
      state: this._applicationState,
      relation: queryDraftEffectRelation(this._appliedSource, this._draftText),
      label: indicatorText, detail: detailedText,
    });
  }

  renderExpression(bundle) {
    const queryTextRelation = queryDraftEffectRelation(this._appliedSource, this._draftText);
    const catalog = bundle.state?.catalog || {};
    const projection = {
      selectionElement: this._elements?.searchSelection,
      languagePresentationRevision: this._languagePresentationRevision,
      acknowledgedDsl: this._acknowledgedDsl,
      acknowledgedExpression: this._acknowledgedExpression,
      acknowledgedRawExpressionTree: this._acknowledgedRawExpressionTree,
      appliedSource: this._appliedSource,
      queryTextRelation,
      fishCatalog: catalog.fish,
      itemCatalog: catalog.items,
      patchCatalog: catalog.patches,
      semanticTermCatalog: catalog.semanticTerms,
      tradeNpcCatalog: catalog.tradeNpcs,
      zoneCatalog: catalog.zones,
    };
    if (sameAcknowledgedExpressionProjection(this._lastExpressionProjection, projection)) {
      return;
    }
    const fishLookup = buildQueryInputFishIdentityLookup(catalog.fish);
    const itemLookup = buildQueryInputItemIdentityLookup(catalog.items);
    const tradeNpcLookup = buildQueryInputTradeNpcIdentityLookup(catalog.tradeNpcs);
    const fishFilterMetadataByTerm = Object.fromEntries(
      ["favourite", "missing", "missing-titles", "red", "yellow", "blue", "green", "white"]
        .map((term) => [term, fishFilterTermMetadata(term)]),
    );
    const renderBundle = {
      ...bundle,
      inputState: {
        ...bundle.inputState,
        search: {
          ...bundle.inputState.search,
          expression: cloneJson(this._acknowledgedExpression || {
            type: "group",
            operator: "or",
            children: [],
          }),
          rawExpression: this._appliedSource,
          rawExpressionTree: cloneRawExpressionTree(this._acknowledgedRawExpressionTree),
          expressionIssues: [],
        },
      },
    };
    renderSearchSelection(this._elements, renderBundle, fishLookup, {
      buildSemanticTermLookup,
      setBooleanProperty,
      setTextContent,
      escapeHtml,
      fishFilterTermIconMarkup,
      fishIdentityMarkup,
      itemLookup,
      zoneIdentityMarkup,
      formatZone,
      contestFish: this._elements.contest?.selectedFish,
      titles: catalog.titles,
      semanticIdentityMarkup,
      tradeNpcIdentityMarkup,
      tradeNpcLookup,
      resolveFishGrade,
      fishFilterTermMetadata: fishFilterMetadataByTerm,
      sourceText: this._appliedSource,
      rawExpressionTree: this._acknowledgedRawExpressionTree,
      queryTextRelation,
    });
    this._ribbonLayoutPending = true;
    this._lastExpressionProjection = projection;
  }

  layoutExpressionRibbon() {
    if (!this._ribbonLayoutPending) {
      return false;
    }
    const ribbon = this._elements?.searchSelection?.querySelector?.(
      ".fishy-applied-query-rail",
    );
    if (!ribbon) {
      this._ribbonLayoutPending = false;
      return false;
    }
    if (!layoutQueryRibbon(ribbon)) {
      return false;
    }
    this._ribbonLayoutPending = false;
    return true;
  }

  cancelPaintAlignedPresentation() {
    this._presentationFrameRevision += 1;
    if (
      this._presentationFrameKind === "animation-frame" &&
      this._presentationFrameHandle !== null &&
      typeof globalThis.cancelAnimationFrame === "function"
    ) {
      globalThis.cancelAnimationFrame(this._presentationFrameHandle);
    }
    this._presentationFrameHandle = null;
    this._presentationFrameKind = "";
    this._revealCaretOnNextPresentation = false;
  }

  schedulePaintAlignedPresentation({ revealCaret = false } = {}) {
    if (revealCaret) {
      this._revealCaretOnNextPresentation = true;
    }
    if (
      (!this._ribbonLayoutPending && !this._revealCaretOnNextPresentation) ||
      this._presentationFrameHandle !== null
    ) {
      return;
    }

    const revision = ++this._presentationFrameRevision;
    const present = () => {
      if (revision !== this._presentationFrameRevision) {
        return;
      }
      this._presentationFrameHandle = null;
      this._presentationFrameKind = "";
      if (this.isConnected === false) {
        this._revealCaretOnNextPresentation = false;
        return;
      }
      const revealPending = this._revealCaretOnNextPresentation;
      this._revealCaretOnNextPresentation = false;
      if (revealPending) {
        this._queryScrollController?.revealCaretEdge();
      }
      this.layoutExpressionRibbon();
    };

    if (typeof globalThis.requestAnimationFrame === "function") {
      this._presentationFrameKind = "animation-frame";
      this._presentationFrameHandle = globalThis.requestAnimationFrame(present);
      return;
    }
    this._presentationFrameKind = "microtask";
    this._presentationFrameHandle = true;
    scheduleMicrotask(present);
  }

  render() {
    if (!this._mapShellProjection || !this._elements?.draftInput) {
      return;
    }
    const bundle = this.stateBundle();
    const activeDsl = activeExpressionDslFromBundle(bundle);
    if (!this._dispatchingActivationRequest && activeDsl !== this._lastActiveDsl) {
      this.syncDraftFromActive({ force: true, writeInput: true });
      return this.render();
    }
    const sourceChanged = this.renderDraftInput();
    this.renderStatus(bundle);
    this.renderExpression(bundle);
    this.renderSuggestions(bundle);
    this.schedulePaintAlignedPresentation({
      revealCaret: this._editing && sourceChanged,
    });
  }

  scheduleRender() {
    if (this._renderQueued) {
      return;
    }
    this._renderQueued = true;
    scheduleMicrotask(() => {
      this._renderQueued = false;
      if (this.isConnected === false) {
        return;
      }
      this.render();
    });
  }
}

export function registerFishyMapQueryInputElement(registry = globalThis.customElements) {
  if (!registry || typeof registry.get !== "function" || typeof registry.define !== "function") {
    return false;
  }
  if (registry.get(QUERY_INPUT_TAG_NAME)) {
    return true;
  }
  registry.define(QUERY_INPUT_TAG_NAME, FishyMapQueryInputElement);
  return true;
}

registerFishyMapQueryInputElement();
